import { useRef, useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    Chip,
    CircularProgress
} from "@mui/material";
import {
    Close as CloseIcon,
    ArrowForward as BackIcon,
    Print as PrintIcon,
    Download as DownloadIcon,
    CheckCircle as CheckIcon,
    Cancel as XIcon,
    Description as FileIcon
} from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getFormById } from "../../services/FormTemplateService";
import { t as coachT } from "../dashboard/coach/coachTokens";

const FormViewDialog = ({ open, onClose, form, clientName, coach = false }) => {
    const contentRef = useRef(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(null);

    // Fetch full form data when dialog opens
    useEffect(() => {
        if (open && form?.id) {
            loadFormData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form?.id]);

    const loadFormData = async () => {
        setLoading(true);
        try {
            const response = await getFormById(form.id);
            setFormData(response.data);
        } catch (error) {
            console.error('Error loading form data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Find signature URL from answers array
    const signatureAnswer = formData?.answers?.find(a => a.answerType === 'SIGNATURE');
    const signatureUrl = signatureAnswer?.signatureImageUrl || null;

    // Build questionnaire data from API response
    const questionnaire = formData ? {
        id: formData.id,
        title: formData.formTemplateName || formData.name || "שאלון",
        status: formData.status || "completed",
        sentDate: formData.createdDate ? new Date(formData.createdDate * 1000).toLocaleDateString('he-IL') : "",
        completedDate: formData.completedDate ? new Date(formData.completedDate * 1000).toLocaleDateString('he-IL') : "",
        questions: formData.questions || [],
        answers: formData.answers || []
    } : null;

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPDF = async () => {
        if (!contentRef.current) return;

        setGeneratingPdf(true);

        // Get references to DOM elements
        const dialogPaper = contentRef.current.closest('.MuiDialog-paper');
        const dialogContent = contentRef.current;

        // Store original styles to restore later
        const originalPaperStyles = dialogPaper ? {
            maxHeight: dialogPaper.style.maxHeight,
            overflow: dialogPaper.style.overflow
        } : null;
        const originalContentStyles = {
            maxHeight: dialogContent.style.maxHeight,
            overflow: dialogContent.style.overflow,
            overflowY: dialogContent.style.overflowY
        };

        // Helper function to restore styles
        const restoreStyles = () => {
            if (dialogPaper && originalPaperStyles) {
                dialogPaper.style.maxHeight = originalPaperStyles.maxHeight || '';
                dialogPaper.style.overflow = originalPaperStyles.overflow || '';
            }
            dialogContent.style.maxHeight = originalContentStyles.maxHeight || '';
            dialogContent.style.overflow = originalContentStyles.overflow || '';
            dialogContent.style.overflowY = originalContentStyles.overflowY || '';
        };

        try {
            // Temporarily remove scroll/overflow constraints to capture full content
            if (dialogPaper) {
                dialogPaper.style.maxHeight = 'none';
                dialogPaper.style.overflow = 'visible';
            }
            dialogContent.style.maxHeight = 'none';
            dialogContent.style.overflow = 'visible';
            dialogContent.style.overflowY = 'visible';

            // Wait for DOM to update
            await new Promise(resolve => setTimeout(resolve, 100));

            // Create canvas from the content - now capturing the full scrollable area
            const canvas = await html2canvas(contentRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                height: contentRef.current.scrollHeight,
                windowHeight: contentRef.current.scrollHeight
            });

            // Restore styles immediately after capturing
            restoreStyles();

            const imgData = canvas.toDataURL('image/png');

            // Calculate PDF dimensions (A4)
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            // Margins
            const marginLeft = 10;
            const marginRight = 10;
            const marginTop = 15;
            const marginBottom = 15;

            const contentWidth = pdfWidth - marginLeft - marginRight;
            const contentHeight = pdfHeight - marginTop - marginBottom;

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            // Scale image to fit width
            const ratio = contentWidth / imgWidth;
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;

            if (scaledHeight <= contentHeight) {
                // Content fits on one page
                pdf.addImage(imgData, 'PNG', marginLeft, marginTop, scaledWidth, scaledHeight);
            } else {
                // Content needs multiple pages - slice the image
                const pageHeightInPixels = contentHeight / ratio;
                let yPosition = 0;
                let pageNum = 0;

                while (yPosition < imgHeight) {
                    if (pageNum > 0) {
                        pdf.addPage();
                    }

                    // Create a temporary canvas for this page slice
                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = imgWidth;
                    pageCanvas.height = Math.min(pageHeightInPixels, imgHeight - yPosition);

                    const ctx = pageCanvas.getContext('2d');
                    ctx.drawImage(
                        canvas,
                        0, yPosition,
                        imgWidth, pageCanvas.height,
                        0, 0,
                        imgWidth, pageCanvas.height
                    );

                    const pageImgData = pageCanvas.toDataURL('image/png');
                    const pageScaledHeight = pageCanvas.height * ratio;

                    pdf.addImage(pageImgData, 'PNG', marginLeft, marginTop, scaledWidth, pageScaledHeight);

                    yPosition += pageHeightInPixels;
                    pageNum++;
                }
            }

            // Download the PDF
            const fileName = `${questionnaire?.title || 'טופס'}_${clientName || 'מתאמן'}.pdf`;
            pdf.save(fileName);
        } catch (error) {
            // Always restore styles on error
            restoreStyles();
            console.error('Error generating PDF:', error);
            alert('שגיאה ביצירת ה-PDF');
        } finally {
            setGeneratingPdf(false);
        }
    };

    if (!form) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    direction: "rtl",
                    maxHeight: "90vh",
                    "@media print": {
                        maxHeight: "none",
                        borderRadius: 0,
                        boxShadow: "none",
                        margin: 0,
                        padding: "20mm"
                    }
                }
            }}
        >
            <DialogTitle sx={{
                padding: "20px 24px",
                borderBottom: "1px solid #E2E8F0"
            }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start"
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                            <BackIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{
                                fontSize: "18px",
                                fontWeight: 600,
                                color: "#1E293B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                {questionnaire?.title}
                            </Typography>
                            <Typography sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                marginTop: "4px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                מתאמן: {clientName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            onClick={handlePrint}
                            variant="outlined"
                            startIcon={<PrintIcon sx={{ marginLeft: "8px" }} />}
                            size="small"
                            sx={{
                                borderColor: "#E2E8F0",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                "&:hover": {
                                    borderColor: "#CBD5E1",
                                    backgroundColor: "#F8FAFC"
                                }
                            }}
                        >
                            הדפסה
                        </Button>
                        <Button
                            onClick={handleDownloadPDF}
                            variant="contained"
                            disabled={generatingPdf}
                            startIcon={generatingPdf ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ marginLeft: "8px" }} />}
                            size="small"
                            sx={{
                                backgroundColor: coach ? coachT.green : "#57837E",
                                borderRadius: coach ? coachT.rs : undefined,
                                boxShadow: coach ? "none" : undefined,
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                "&:hover": {
                                    backgroundColor: coach ? coachT.greenDark : "#46706B",
                                    boxShadow: coach ? "none" : undefined
                                }
                            }}
                        >
                            {generatingPdf ? "מייצר PDF..." : "שמירה כ-PDF"}
                        </Button>
                        <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Status & Dates */}
                <Paper
                    sx={{
                        marginTop: "16px",
                        padding: "12px 16px",
                        backgroundColor: "#F8FAFC",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: 4
                    }}
                    elevation={0}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: "14px", color: "#64748B" }}>סטטוס:</Typography>
                        <Chip
                            label={questionnaire?.status === "completed" ? "הושלם" : "ממתין למילוי"}
                            size="small"
                            sx={{
                                backgroundColor: questionnaire?.status === "completed" ? "#D1FAE5" : "#FEF3C7",
                                color: questionnaire?.status === "completed" ? "#10B981" : "#F59E0B",
                                fontWeight: 600,
                                fontSize: "12px"
                            }}
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: "14px", color: "#64748B" }}>תאריך שליחה:</Typography>
                        <Typography sx={{ fontSize: "14px", color: "#1E293B", fontWeight: 500 }}>
                            {questionnaire?.sentDate}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: "14px", color: "#64748B" }}>תאריך השלמה:</Typography>
                        <Typography sx={{ fontSize: "14px", color: "#1E293B", fontWeight: 500 }}>
                            {questionnaire?.completedDate || "-"}
                        </Typography>
                    </Box>
                </Paper>
            </DialogTitle>

            <DialogContent ref={contentRef} sx={{
                padding: "24px",
                paddingTop: "32px",
                backgroundColor: "#ffffff",
                "@media print": {
                    padding: "0",
                    paddingTop: "20px"
                }
            }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
                        <CircularProgress sx={{ color: coach ? coachT.green : "#57837E" }} />
                    </Box>
                ) : (
                    <>
                        {/* Answers Section */}
                        <Paper
                            sx={{
                                padding: "20px",
                                backgroundColor: "#FFFFFF",
                                borderRadius: "8px",
                                border: "1px solid #E2E8F0",
                                marginBottom: "16px"
                            }}
                            elevation={0}
                        >
                            <Typography sx={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "16px",
                                paddingBottom: "12px",
                                borderBottom: "1px solid #E2E8F0",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                תשובות הטופס
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                {questionnaire?.questions?.map((question, index) => {
                                    // Find matching answer
                                    const answer = questionnaire.answers?.find(a => a.questionId === question.id);

                                    // Skip display-only types
                                    if (['FORM_TITLE', 'SECTION_TITLE', 'FREE_TEXT', 'LINK', 'PDF_ATTACHMENT', 'SIGNATURE'].includes(question.questionType)) {
                                        if (question.questionType === 'FORM_TITLE') {
                                            return (
                                                <Typography key={index} sx={{ fontSize: "20px", fontWeight: 600, color: "#1E293B", textAlign: "center" }}>
                                                    {question.title}
                                                </Typography>
                                            );
                                        }
                                        if (question.questionType === 'SECTION_TITLE') {
                                            return (
                                                <Typography key={index} sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B", borderBottom: "2px solid #57837E", paddingBottom: "8px" }}>
                                                    {question.title}
                                                </Typography>
                                            );
                                        }
                                        return null;
                                    }

                                    return (
                                        <Box key={index}>
                                            <Typography sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: "#1E293B",
                                                marginBottom: "8px",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }}>
                                                {question.question}
                                            </Typography>

                                            {/* TEXT types */}
                                            {['SHORT_TEXT', 'LONG_TEXT', 'EMAIL', 'PHONE'].includes(question.questionType) && (
                                                <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px" }}>
                                                    {answer?.text || "-"}
                                                </Typography>
                                            )}

                                            {/* YES_NO */}
                                            {question.questionType === 'YES_NO' && (
                                                <Box sx={{ paddingRight: "16px" }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        {answer?.answer === true ? (
                                                            <>
                                                                <CheckIcon sx={{ color: "#10B981", fontSize: "20px" }} />
                                                                <Typography sx={{ fontSize: "14px", color: "#64748B" }}>כן</Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <XIcon sx={{ color: "#EF4444", fontSize: "20px" }} />
                                                                <Typography sx={{ fontSize: "14px", color: "#64748B" }}>לא</Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                    {answer?.answer === true && answer?.details && (
                                                        <Typography sx={{ fontSize: "13px", color: "#64748B", marginTop: "8px", paddingRight: "28px" }}>
                                                            <Typography component="span" sx={{ color: "#94A3B8" }}>פרטים נוספים: </Typography>
                                                            {answer.details}
                                                        </Typography>
                                                    )}
                                                    {answer?.answer === false && answer?.detailsNo && (
                                                        <Typography sx={{ fontSize: "13px", color: "#64748B", marginTop: "8px", paddingRight: "28px" }}>
                                                            <Typography component="span" sx={{ color: "#94A3B8" }}>פרטים נוספים: </Typography>
                                                            {answer.detailsNo}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}

                                            {/* MULTIPLE_CHOICE */}
                                            {question.questionType === 'MULTIPLE_CHOICE' && (
                                                <Box sx={{ paddingRight: "16px", display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {(answer?.selectedOptions || []).map((option, idx) => (
                                                        <Chip key={idx} label={option} size="small" sx={{ backgroundColor: "rgba(87, 131, 126, 0.1)", color: "#57837E", fontSize: "13px" }} />
                                                    ))}
                                                </Box>
                                            )}

                                            {/* NUMBER */}
                                            {question.questionType === 'NUMBER' && (
                                                <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px" }}>
                                                    {answer?.number ?? "-"}
                                                </Typography>
                                            )}

                                            {/* DATE */}
                                            {question.questionType === 'DATE' && (
                                                <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px" }}>
                                                    {Array.isArray(answer?.date) ? `${answer.date[2]}/${answer.date[1]}/${answer.date[0]}` : (answer?.date || "-")}
                                                </Typography>
                                            )}

                                            {/* RATING */}
                                            {question.questionType === 'RATING' && (
                                                <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px" }}>
                                                    {answer?.rating || 0} / {question.maxRating || 5}
                                                </Typography>
                                            )}

                                            {/* CHECKBOX */}
                                            {question.questionType === 'CHECKBOX' && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, paddingRight: "16px" }}>
                                                    {answer?.checked ? (
                                                        <><CheckIcon sx={{ color: "#10B981", fontSize: "20px" }} /><Typography sx={{ fontSize: "14px", color: "#64748B" }}>מאושר</Typography></>
                                                    ) : (
                                                        <><XIcon sx={{ color: "#EF4444", fontSize: "20px" }} /><Typography sx={{ fontSize: "14px", color: "#64748B" }}>לא מאושר</Typography></>
                                                    )}
                                                </Box>
                                            )}

                                            {/* FILE_UPLOAD */}
                                            {question.questionType === 'FILE_UPLOAD' && answer?.uploadedFileUrls?.length > 0 && (
                                                <Box sx={{ paddingRight: "16px" }}>
                                                    <Button
                                                        variant="text"
                                                        href={answer.uploadedFileUrls[0]}
                                                        target="_blank"
                                                        startIcon={<FileIcon sx={{ marginLeft: "8px" }} />}
                                                        sx={{ color: "#57837E", fontSize: "14px", textDecoration: "underline" }}
                                                    >
                                                        צפה בקובץ
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Paper>

                        {/* Signature - only show if form has a signature question */}
                        {questionnaire?.questions?.some(q => q.questionType === 'SIGNATURE') && (
                            <Paper
                                sx={{
                                    padding: "20px",
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "8px",
                                    border: "1px solid #E2E8F0"
                                }}
                                elevation={0}
                            >
                                <Typography sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    marginBottom: "16px",
                                    paddingBottom: "12px",
                                    borderBottom: "1px solid #E2E8F0",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}>
                                    חתימה דיגיטלית
                                </Typography>

                                {signatureUrl ? (
                                    <Box>
                                        <Paper
                                            sx={{
                                                padding: "16px",
                                                backgroundColor: "#FFFFFF",
                                                borderRadius: "8px",
                                                border: "2px solid #E2E8F0",
                                                maxWidth: "400px",
                                                overflow: "hidden"
                                            }}
                                            elevation={0}
                                        >
                                            <img
                                                src={signatureUrl}
                                                alt="חתימה דיגיטלית"
                                                crossOrigin="anonymous"
                                                style={{
                                                    width: "100%",
                                                    height: "150px",
                                                    objectFit: "contain",
                                                    backgroundColor: "#FFFFFF"
                                                }}
                                            />
                                        </Paper>
                                        <Typography sx={{
                                            fontSize: "13px",
                                            color: "#64748B",
                                            marginTop: "8px",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}>
                                            נחתם בתאריך {questionnaire?.completedDate || "-"}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography sx={{
                                        fontSize: "14px",
                                        color: "#94A3B8",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}>
                                        לא נמצאה חתימה
                                    </Typography>
                                )}
                            </Paper>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FormViewDialog;
